import React, { Component, PureComponent } from "react";
import { Progress } from "antd";
import styled from "styled-components";
import { FlipNumbers, Color, GlobalColorPalette, ColorProps, ITable, ChartProps, Chart, ICSS, ICSSProps } from "@gago/frame";

// tslint:disable:variable-name ter-max-len
const GaugeAntdContainer = styled.div<ColorProps<GlobalColorPalette>>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  .container {
    height: 140px;
    width: 140px;
    flex: 1;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    .popup {
      display: none;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) scale(1);
      padding: 4px;
      font-size: 12px;
      color: #fff;
      text-align: center;
      background-color: #051b31;
      > * {
        white-space: nowrap;
      }
    }
    &:hover {
      .popup {
        display: block;
      }
    }
    .progress-title {
      position: absolute;
      bottom: 8px;
      left: 50%;
      transform: translateX(-50%) scale(1);
      font-size: 12px;
      font-weight: 500;
      color: #eafbff;
      white-space: nowrap;
      line-height: 1;
    }
    .ant-progress-circle  {
      transition: stroke-dashoffset 0.3s ease 0s, stroke-dasharray 0.3s ease 0s, stroke 0.8s ease, stroke-width 0.8s ease;
    }
    .ant-progress-circle-trail {
      stroke: rgba(255, 255, 255, 0.09) !important;
    }
    .ant-progress-circle-gradient .ant-progress-circle-path {
      stroke: ${props => `url(${props.colorPalette.subColor[0]})`} !important;
    }
  }
`;
// tslint:disable-next-line:completed-docs
const GaugePointer = styled.div<ColorProps<GlobalColorPalette> & {rate: number}>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  .finger-container {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 10px;
    border-right: 0;
    transform-origin: center;
    transform: translate(-50%, -50%) rotate(${props => `${props.rate * 270 + 135}deg`});
    transition: all 0.8s ease;
    &::after {
      box-sizing: content-box;
      content: "";
      position: absolute;
      right: -3px;
      width: 12px;
      height: 12px;
      border: 1px solid ${props => props.colorPalette.subColor[0]};
      border-radius: 50%;
      background: #fff;
    }
    .finger-content {
      position: absolute;
      left: 50%;
      top: -1px;
      right: 24px;
      bottom: -1px;
      &::before {
        content: "";
        position: absolute;
        left: -6px;
        width: 13px;
        height: 12px;
        background: #ec2556;
        border-radius: 6px ;
      }
      &::after {
        content: "";
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: -1px;
        width: 3px;
        height: 2px;
        background: #ec2556;
        border-radius: 50%;
      }
      .finger {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        background: #ec2556;
        clip-path: polygon(100% 5px, 100% 7px, 0 100%, 0 0);
      }
    }
  }
  .pointer {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    background-color: #ccc;
    border-radius: 50%;
  }
`;

// tslint:disable-next-line:completed-docs
const AxisTick = styled.div<{rotate: number}>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: calc(50% - 13px);
  height: 1px;
  transform-origin: 0 50%;
  transform: rotate(${props => `${props.rotate}deg`});
  &::after {
    content: "";
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    opacity: 0.6;
    border-radius: 0.5px;
    background: #d8d8d8;
  }
`;

// tslint:disable-next-line:completed-docs
const AxisTickHighlight = styled.div<{rotate: number}>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: calc(50% - 14px);
  height: 2px;
  transform-origin: 0 50%;
  transform: rotate(${props => `${props.rotate}deg`});
  &::after {
    content: "";
    position: absolute;
    right: -2px;
    top: 0;
    bottom: 0;
    width: 8px;
    border-radius: 2px;
    background: #ffd83d;
  }
`;

type MyGaugeProps = ColorProps<GlobalColorPalette> & ChartProps<1, 5> & ICSSProps;

interface MyGaugeState {
  /** 仪表盘大小 */
  size: number;
}

/**
 * 大屏仪表盘  只针对数据比例
 * @author yangyongheng
 * @date 2020-06-29
 * @export
 * @class MyGaugeCopy
 * @extends {PureComponent<MyGaugeProps, MyGaugeState>}
 * @implements {Color<GlobalColorPalette>}
 * @implements {Chart<1, 5>}
 * @implements {ICSS}
 */
export default class MyGaugeCopy extends PureComponent<MyGaugeProps, MyGaugeState> implements Color<GlobalColorPalette>, Chart<1, 5>, ICSS {
  gaugeRef: Element | null = null;
  constructor(props: MyGaugeProps) {
    super(props);
    this.state = {
      size: 120,
    };
  }
  componentDidMount() {
    if (this.gaugeRef) {
      const width = this.gaugeRef.clientWidth;
      const height = this.gaugeRef.clientHeight;
      const size = Math.min(width, height);
      this.setState({ size });
    }
  }
  render() {
    // tslint:disable:jsx-no-multiline-js jsx-no-lambda binary-expression-operand-order
    const { colorPalette, chartData, style, className } = this.props;
    const { size } = this.state;
    const signRotate = chartData.data[0][2] * 270;
    const rate = chartData.data[0][3];
    return (
      <GaugeAntdContainer ref={node => this.gaugeRef = node} style={style} className={className} colorPalette={colorPalette}>
        <div className="container">
          <div className="progress-title">{chartData.data[0][1]}</div>
          {/* 修复svg渐变多个实例共用一个配色方案 */}
          {/* <svg style={{ position: "absolute", width: 0, right: 0 }}>
            <defs>
              <linearGradient id={colorPalette.subColor[0]} x1="100%" y1="0%" x2="0%" y2="0%">
                <stop offset="0%" stopColor={colorPalette.subColor[0]} />
                <stop offset="100%" stopColor={colorPalette.subColor[1]} />
              </linearGradient>
            </defs>
          </svg> */}
          <Progress
            type="dashboard"
            percent={Math.abs(rate * 100)}
            width={size}
            strokeWidth={6}
            strokeColor={{
              from: colorPalette.subColor[0],
              to: colorPalette.subColor[1],
            }}
            format={() => ""}
          />
          <GaugePointer colorPalette={colorPalette} rate={Math.abs(rate)}>
            <div className="finger-container" >
              <div className="finger-content" ><div className="finger" /></div>
            </div>
            <div className="pointer" />
          </GaugePointer>
          {Array(28).fill("").map((item, index) =>
            <AxisTick
              key={index}
              rotate={10 * index + 135}
            />,
          )}
          <AxisTickHighlight
            rotate={signRotate + 135}
            style={{ height: "4px" }}
          />
          <div className="popup">
            <div>{chartData.head[4]}</div>
            <div>{chartData.data[0][4]} {chartData.suffix && chartData.suffix[4]}</div>
          </div>
        </div>
      </GaugeAntdContainer>
    );
  }
}
